/**
 * Pure normalizer functions for `StoredAnalysisPayload` → `AnalysisResult`.
 *
 * These helpers encapsulate every quirk that exists between what the database
 * stores and what the frontend `AnalysisResult` type expects:
 *
 * - Direction: DB uses `'LONG'`/`'SHORT'` (Prisma enum casing); the shared
 *   type expects lowercase (`'long'`/`'short'`) on `AnalysisResult.direction`
 *   and uppercase (`'LONG'`/`'SHORT'`) on `AnalysisSetup.direction`.
 * - Methodology: DB stores `'price_action'`; the shared type uses `'price-action'`.
 * - Markers: legacy DB column is `markers`; the shared type surfaces it as
 *   `chartMarkers`.
 * - Setup: new records carry a full `setup` object; legacy records only have
 *   flat top-level fields (`entry`, `stopLoss`, etc.) that must be assembled
 *   into an `AnalysisSetup`.
 */

import type {
  AnalysisMethodology,
  AnalysisSetup,
  ChartMarker,
} from '@trading/shared';
import type { StoredAnalysisPayload } from './analysisStore.js';

// ---------------------------------------------------------------------------
// Internal direction maps (kept local — callers use the normalizer functions)
// ---------------------------------------------------------------------------

/**
 * Maps any direction casing returned by the DB to the lowercase form that
 * `AnalysisResult.direction` expects.
 */
const DIRECTION_MAP = {
  LONG: 'long',
  SHORT: 'short',
  long: 'long',
  short: 'short',
} as const satisfies Record<NonNullable<StoredAnalysisPayload['direction']>, 'long' | 'short'>;

/**
 * Maps any direction casing returned by the DB to the uppercase form that
 * `AnalysisSetup.direction` expects.
 */
const UPPER_DIRECTION_MAP = {
  LONG: 'LONG',
  SHORT: 'SHORT',
  long: 'LONG',
  short: 'SHORT',
} as const satisfies Record<NonNullable<StoredAnalysisPayload['direction']>, 'LONG' | 'SHORT'>;

// ---------------------------------------------------------------------------
// Exported normalizer functions
// ---------------------------------------------------------------------------

/**
 * Converts the raw DB direction value to the lowercase form used by
 * `AnalysisResult.direction` (`'long' | 'short' | null`).
 */
export function normalizeDirection(
  raw: StoredAnalysisPayload['direction'],
): 'long' | 'short' | null {
  if (raw == null) return null;
  return DIRECTION_MAP[raw];
}

/**
 * Converts the raw DB methodology value to the canonical `AnalysisMethodology`
 * form, mapping the DB-only `'price_action'` variant to the shared type's
 * `'price-action'`.
 */
export function normalizeMethodology(
  raw: StoredAnalysisPayload['methodology'],
): AnalysisMethodology {
  return raw === 'price_action' ? 'price-action' : raw;
}

/**
 * Builds a normalised `AnalysisSetup` from a `StoredAnalysisPayload`.
 *
 * Two shapes are handled:
 * - **New format** — the payload carries a full `setup` object (detected by
 *   `setup.entry !== undefined`). Fields missing from `setup` fall back to
 *   their top-level counterparts for maximum back-compat.
 * - **Legacy format** — the payload only has flat top-level fields; an
 *   `AnalysisSetup` is reconstructed from them.
 *
 * @note An entry price of `0` is treated as absent (`valid: false`) because
 * the truthiness check `!!analysis.entry` is used for the `valid` fallback.
 * This mirrors the original inline behaviour and is intentional: a zero
 * entry price is not a meaningful trade setup for this application.
 */
export function normalizeSetup(analysis: StoredAnalysisPayload): AnalysisSetup {
  const setup = analysis.setup;
  const hasFullSetup = setup != null && setup.entry !== undefined;

  const rawDirection = analysis.direction;
  const upperDirection =
    rawDirection != null ? UPPER_DIRECTION_MAP[rawDirection] : null;

  if (hasFullSetup) {
    return {
      valid: setup.valid ?? !!analysis.entry,
      direction: setup.direction ?? upperDirection,
      confidence: setup.confidence ?? analysis.confidence ?? 0,
      entry: setup.entry,
      stopLoss: setup.stopLoss,
      targets: setup.targets ?? analysis.targets ?? null,
      riskReward: setup.riskReward ?? analysis.riskReward ?? null,
      riskPercent: setup.riskPercent ?? analysis.riskPercent ?? null,
      invalidation: setup.invalidation ?? null,
    };
  }

  // Legacy flat-field reconstruction
  return {
    valid: !!analysis.entry,
    direction: upperDirection,
    confidence: analysis.confidence ?? 0,
    entry: analysis.entry
      ? { price: analysis.entry, type: 'limit' as const, reasoning: '' }
      : null,
    stopLoss: analysis.stopLoss
      ? { price: analysis.stopLoss, reasoning: '' }
      : null,
    targets: analysis.targets ?? null,
    riskReward: analysis.riskReward ?? null,
    riskPercent: analysis.riskPercent ?? null,
    invalidation: null,
  };
}

/**
 * Resolves the `chartMarkers` array from a `StoredAnalysisPayload`, handling
 * the legacy migration where the DB column was called `markers` before being
 * renamed to `chartMarkers` in the API response.
 *
 * Precedence: `chartMarkers` (new) → `markers` (legacy) → `[]`.
 */
export function normalizeMarkers(
  analysis: Pick<StoredAnalysisPayload, 'chartMarkers' | 'markers'>,
): ChartMarker[] {
  return analysis.chartMarkers ?? analysis.markers ?? [];
}
